import React, { useCallback, useEffect, useMemo, useState } from "react";
import Hero from "./Hero";
import Results from "./Results";
import Features from "./Features";
import HowItWorks from "./HowItWorks";
import Waitlist from "./Waitlist";
import Pricing from "./Pricing";
import Footer from "./Footer";
import HireTesterModal from "./HireTesterModal";
import { ThemeProvider, useTheme } from "./theme";
import { detectSimulatedMode, resolveApiBase } from "./config";

export default function LaunchSafelyPage() {
  return (
    <ThemeProvider>
      <PageContent />
    </ThemeProvider>
  );
}

function PageContent() {
  const { theme, mode, toggleMode } = useTheme();
  const simulatedMode = detectSimulatedMode();
  const [showResults, setShowResults] = useState(false);
  const [targetUrl, setTargetUrl] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [serverStatus, setServerStatus] = useState<"waking" | "ready" | "error">(
    simulatedMode ? "ready" : "waking"
  );
  const [serverMessage, setServerMessage] = useState<string>(
    simulatedMode ? "Demo mode active. Backend not required." : "Waking backend..."
  );
  const [wakeSequence, setWakeSequence] = useState(0);
  const [showHireModal, setShowHireModal] = useState(false);

  const apiBase = useMemo(() => resolveApiBase(), []);

  useEffect(() => {
    let cancelled = false;
    if (simulatedMode) {
      setServerStatus("ready");
      setServerMessage("Demo mode active. Backend not required.");
      return () => {
        cancelled = true;
      };
    }
    const maxAttempts = 3;

    const attempt = (count: number) => {
      if (cancelled) return;
      setServerStatus("waking");
      setServerMessage(count === 1 ? "Waking backend..." : `Waking backend (attempt ${count})...`);

      const healthUrl = apiBase ? `${apiBase}/health` : "/health";
      fetch(healthUrl, { cache: "no-store" })
        .then((res) => {
          if (!res.ok) throw new Error("bad status");
          if (cancelled) return;
          setServerStatus("ready");
          setServerMessage("Runner ready.");
        })
        .catch(() => {
          if (cancelled) return;
          if (count < maxAttempts) {
            setTimeout(() => attempt(count + 1), 2000);
          } else {
            setServerStatus("error");
            setServerMessage("Backend unreachable. Retry when ready.");
          }
        });
    };

    attempt(1);

    return () => {
      cancelled = true;
    };
  }, [wakeSequence, apiBase, simulatedMode]);

  const retryWake = useCallback(() => {
    setWakeSequence((seq) => seq + 1);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleOpenHire = () => setShowHireModal(true);
    window.addEventListener("launchSafely:openHireTester", handleOpenHire);
    return () => window.removeEventListener("launchSafely:openHireTester", handleOpenHire);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const previousBodyMargin = document.body.style.margin;
    const previousBodyBackground = document.body.style.background;
    const previousHtmlBackground = document.documentElement.style.background;

    document.body.style.margin = "0";
    document.body.style.background = theme.palette.background;
    document.documentElement.style.background = theme.palette.background;

    return () => {
      document.body.style.margin = previousBodyMargin;
      document.body.style.background = previousBodyBackground;
      document.documentElement.style.background = previousHtmlBackground;
    };
  }, [theme.palette.background]);

  // Render Results.tsx inline within Hero.tsx so the test report appears near the demo runner
  const resultsPanel = showResults ? (
    <Results
      show
      demoTargetUrl={targetUrl ?? undefined}
      runId={runId ?? undefined}
      onHireRequest={() => setShowHireModal(true)}
    />
  ) : null;

  return (
    <div
      style={{
        background: theme.palette.background,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        color: theme.palette.textPrimary,
        transition: "background 0.4s ease, color 0.4s ease",
      }}
    >
      <Hero
        currentTheme={mode}
        onToggleTheme={toggleMode}
        serverStatus={serverStatus}
        serverMessage={serverMessage}
        onRetryServer={retryWake}
        simulatedMode={simulatedMode}
        onComplete={({ targetUrl, runId }) => {
          setTargetUrl(targetUrl);
          setRunId(runId || null);
          setShowResults(true);
        }}
        resultsPanel={resultsPanel}
      />
      <HowItWorks />
      <Features />
      <Waitlist />
      <Pricing />
      <Footer />
      <HireTesterModal open={showHireModal} onClose={() => setShowHireModal(false)} />
    </div>
  );
}
